import { useEffect, useState } from 'react';
import { doc, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import {
  DEFAULT_STUDY_PLANS,
  DEFAULT_EXTENSION_OPTIONS,
  DEFAULT_DEMO_VIDEO_URL,
  DEFAULT_EXAM_FEE,
  DEFAULT_DEGREE_FEE,
  DEFAULT_ADMIN_CONTACT_PHONE,
  DEFAULT_ADMIN_CONTACT_EMAIL,
  normalizePlans,
  normalizePlan,
  normalizeExtensionOptions,
  normalizeExamFee,
  normalizeDegreeFee,
} from '../data/studyPlans';

const PRICING_DOC = doc(db, 'siteSettings', 'pricing');

/**
 * Live study plans + extension pricing + exam/degree fees + demo video URL from Firestore.
 * Falls back to defaults until the doc exists.
 */
export function useStudyPlans() {
  const [plans, setPlans] = useState(() => normalizePlans(DEFAULT_STUDY_PLANS));
  const [extensions, setExtensions] = useState(() =>
    normalizeExtensionOptions(DEFAULT_EXTENSION_OPTIONS)
  );
  const [demoVideoUrl, setDemoVideoUrl] = useState(DEFAULT_DEMO_VIDEO_URL);
  const [examFee, setExamFee] = useState(() => normalizeExamFee(DEFAULT_EXAM_FEE));
  const [degreeFee, setDegreeFee] = useState(() => normalizeDegreeFee(DEFAULT_DEGREE_FEE));
  const [adminContactPhone, setAdminContactPhone] = useState(DEFAULT_ADMIN_CONTACT_PHONE);
  const [adminContactEmail, setAdminContactEmail] = useState(DEFAULT_ADMIN_CONTACT_EMAIL);
  const [loading, setLoading] = useState(true);
  const [fromServer, setFromServer] = useState(false);

  useEffect(() => {
    const unsub = onSnapshot(
      PRICING_DOC,
      (snap) => {
        const data = snap.exists() ? snap.data() : null;
        if (data && Array.isArray(data.plans)) {
          setPlans(normalizePlans(data.plans));
          setFromServer(true);
        } else {
          setPlans(normalizePlans(DEFAULT_STUDY_PLANS));
          setFromServer(false);
        }
        setExtensions(normalizeExtensionOptions(data?.extensions));
        setDemoVideoUrl(
          typeof data?.demoVideoUrl === 'string' ? data.demoVideoUrl : DEFAULT_DEMO_VIDEO_URL
        );
        setExamFee(normalizeExamFee(data?.examFee));
        setDegreeFee(normalizeDegreeFee(data?.degreeFee));
        setAdminContactPhone(
          typeof data?.adminContactPhone === 'string' && data.adminContactPhone.trim()
            ? data.adminContactPhone.trim()
            : DEFAULT_ADMIN_CONTACT_PHONE
        );
        setAdminContactEmail(
          typeof data?.adminContactEmail === 'string' && data.adminContactEmail.trim()
            ? data.adminContactEmail.trim()
            : DEFAULT_ADMIN_CONTACT_EMAIL
        );
        setLoading(false);
      },
      (err) => {
        console.error('Study plans load failed:', err);
        setPlans(normalizePlans(DEFAULT_STUDY_PLANS));
        setExtensions(normalizeExtensionOptions(DEFAULT_EXTENSION_OPTIONS));
        setDemoVideoUrl(DEFAULT_DEMO_VIDEO_URL);
        setExamFee(normalizeExamFee(DEFAULT_EXAM_FEE));
        setDegreeFee(normalizeDegreeFee(DEFAULT_DEGREE_FEE));
        setAdminContactPhone(DEFAULT_ADMIN_CONTACT_PHONE);
        setAdminContactEmail(DEFAULT_ADMIN_CONTACT_EMAIL);
        setFromServer(false);
        setLoading(false);
      }
    );
    return unsub;
  }, []);

  return {
    plans,
    extensions,
    demoVideoUrl,
    examFee,
    degreeFee,
    adminContactPhone,
    adminContactEmail,
    loading,
    fromServer,
  };
}

export async function saveStudyPlans(plans, updatedBy = '') {
  const normalized = normalizePlans(plans).map(normalizePlan);
  await setDoc(
    PRICING_DOC,
    {
      plans: normalized,
      updatedAt: serverTimestamp(),
      updatedBy: updatedBy || '',
    },
    { merge: true }
  );
  return normalized;
}

/** Admin: save extension add-on pricing, demo video, exam/degree fees, contact phone */
export async function savePricingExtras(
  { extensions, demoVideoUrl, examFee, degreeFee, adminContactPhone, adminContactEmail },
  updatedBy = ''
) {
  const payload = { updatedAt: serverTimestamp(), updatedBy: updatedBy || '' };
  if (extensions !== undefined) payload.extensions = normalizeExtensionOptions(extensions);
  if (demoVideoUrl !== undefined) payload.demoVideoUrl = String(demoVideoUrl || '').trim();
  if (examFee !== undefined) payload.examFee = normalizeExamFee(examFee);
  if (degreeFee !== undefined) payload.degreeFee = normalizeDegreeFee(degreeFee);
  if (adminContactPhone !== undefined) {
    payload.adminContactPhone = String(adminContactPhone || '').trim() || DEFAULT_ADMIN_CONTACT_PHONE;
  }
  if (adminContactEmail !== undefined) {
    payload.adminContactEmail = String(adminContactEmail || '').trim() || DEFAULT_ADMIN_CONTACT_EMAIL;
  }
  await setDoc(PRICING_DOC, payload, { merge: true });
  return payload;
}

export async function seedStudyPlansIfMissing(updatedBy = '') {
  const { getDoc } = await import('firebase/firestore');
  const snap = await getDoc(PRICING_DOC);
  if (snap.exists() && Array.isArray(snap.data()?.plans) && snap.data().plans.length) {
    return normalizePlans(snap.data().plans);
  }
  return saveStudyPlans(DEFAULT_STUDY_PLANS, updatedBy);
}

export function getPlanByIdFromList(plans, planId) {
  return plans.find((p) => p.id === planId) || null;
}
